/**
 * Cliente HTTP. Todo el frontend pasa por aquí.
 *
 * En el navegador la base es relativa y Vite o Nginx hacen el proxy. Dentro
 * del APK no hay proxy que valga: la app corre desde el sistema de archivos,
 * así que necesita la URL absoluta del servidor.
 */
import { sesion } from './sesion';

const enApk = !!(window as { Capacitor?: unknown }).Capacitor;

export const API_BASE = enApk
  ? (import.meta.env.VITE_API_URL as string) || 'https://ericmar.care-business.com.mx'
  : '';

export class ErrorApi extends Error {
  codigo: string;
  status: number;
  constructor(mensaje: string, codigo: string, status: number) {
    super(mensaje);
    this.codigo = codigo;
    this.status = status;
  }
}

type Opciones = Omit<RequestInit, 'body'> & { body?: unknown; timeoutMs?: number };

export async function api<T = unknown>(ruta: string, opciones: Opciones = {}): Promise<T> {
  const { body, timeoutMs = 15000, headers, ...resto } = opciones;

  // Sin timeout, una tablet con WiFi malo se queda girando para siempre y el
  // cocinero no sabe si tocó el botón o no.
  const control = new AbortController();
  const temporizador = setTimeout(() => control.abort(), timeoutMs);

  try {
    const respuesta = await fetch(`${API_BASE}/api${ruta}`, {
      ...resto,
      signal: control.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(sesion.token ? { Authorization: `Bearer ${sesion.token}` } : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

    const json = await respuesta.json().catch(() => null);

    if (!respuesta.ok || !json?.ok) {
      const codigo = json?.codigo ?? 'ERROR_RED';
      // Sesión vencida: cerramos aquí, en un solo lugar, en vez de que cada
      // pantalla tenga que acordarse de comprobarlo.
      if (codigo === 'TOKEN_VENCIDO' || codigo === 'TOKEN_INVALIDO') sesion.cerrar();
      throw new ErrorApi(
        json?.mensaje ?? 'No se pudo conectar con el servidor.',
        codigo,
        respuesta.status
      );
    }

    return json.datos as T;
  } catch (err) {
    if (err instanceof ErrorApi) throw err;
    if ((err as Error).name === 'AbortError') {
      throw new ErrorApi('El servidor tardó demasiado en responder.', 'TIMEOUT', 0);
    }
    throw new ErrorApi(
      'Sin conexión con el servidor. Revisa el WiFi del local.',
      'SIN_RED',
      0
    );
  } finally {
    clearTimeout(temporizador);
  }
}
